import { $authHost, $host } from '.'
import {
	CategoryStat,
	CreateMaterialPayload,
	CreateResourcePayload,
	LearningCategory,
	LearningMaterial,
	LearningResource,
	LearningUploadResponse,
	MaterialsResponse,
	RelayTypeStat,
	UpdateMaterialPayload,
	ViewLogResponse
} from '../helpers/types/learning'

// ── Public endpoints ────────────────────────────────────────

export const fetchMaterials = async (params?: {
	page?: number
	limit?: number
	category?: LearningCategory
	relayTypeId?: string
	search?: string
}) => {
	const { data } = await $host.get('/learning', { params })
	return data as MaterialsResponse
}

export const fetchMaterialById = async (id: string) => {
	const { data } = await $host.get(`/learning/${id}`)
	return data as LearningMaterial
}

export const fetchMaterialsByRelayType = async (
	relayTypeId: string,
	page: number = 1,
	limit: number = 20
) => {
	const { data } = await $host.get(
		`/learning/by-relay-type/${relayTypeId}`,
		{ params: { page, limit } }
	)
	return data as MaterialsResponse
}

export const fetchCategoryStats = async () => {
	const { data } = await $host.get('/learning/meta/categories')
	return data as CategoryStat[]
}

export const fetchRelayTypeStats = async () => {
	const { data } = await $host.get('/learning/meta/relay-types')
	return data as RelayTypeStat[]
}

export const fetchDownloadUrl = async (
	materialId: string,
	resourceId: string
) => {
	const { data } = await $host.get(
		`/learning/${materialId}/resources/${resourceId}/download`
	)
	return data as LearningResource
}

// ── Authenticated endpoints ─────────────────────────────────

export const logMaterialView = async (materialId: string) => {
	const { data } = await $authHost.post(
		`/learning/${materialId}/view`
	)
	return data as ViewLogResponse
}

export const pinMaterial = async (materialId: string) => {
	const { data } = await $authHost.post(
		`/learning/${materialId}/pin`
	)
	return data
}

export const unpinMaterial = async (materialId: string) => {
	const { data } = await $authHost.delete(
		`/learning/${materialId}/pin`
	)
	return data
}

export const fetchUserHistory = async (limit: number = 50) => {
	const { data } = await $authHost.get('/learning/user/history', {
		params: { limit }
	})
	return data as LearningMaterial[]
}

export const fetchUserPins = async () => {
	const { data } = await $authHost.get('/learning/user/pins')
	return data as LearningMaterial[]
}

// ── Admin endpoints ─────────────────────────────────────────

export const createMaterialApi = async (
	payload: CreateMaterialPayload
) => {
	const { data } = await $authHost.post('/learning', payload)
	return data as LearningMaterial
}

export const fetchMaterialsAdmin = async (params?: {
	page?: number
	limit?: number
	category?: LearningCategory
	relayTypeId?: string
	search?: string
}) => {
	const { data } = await $authHost.get('/learning/all', { params })
	return data as MaterialsResponse
}

export const updateMaterialApi = async (
	id: string,
	updates: UpdateMaterialPayload
) => {
	const { data } = await $authHost.put(`/learning/${id}`, updates)
	return data as LearningMaterial
}

export const deleteMaterialApi = async (id: string) => {
	const { data } = await $authHost.delete(`/learning/${id}`)
	return data
}

export const publishMaterialApi = async (id: string) => {
	const { data } = await $authHost.patch(
		`/learning/${id}/publish`
	)
	return data as LearningMaterial
}

export const unpublishMaterialApi = async (id: string) => {
	const { data } = await $authHost.patch(
		`/learning/${id}/unpublish`
	)
	return data as LearningMaterial
}

export const addResourceApi = async (
	materialId: string,
	resource: CreateResourcePayload
) => {
	const { data } = await $authHost.post(
		`/learning/${materialId}/resources`,
		resource
	)
	return data as LearningResource
}

export const uploadLearningFileApi = async (file: File) => {
	const formData = new FormData()
	formData.append('file', file)

	const { data } = await $authHost.post(
		'/learning/upload',
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		}
	)

	return data as LearningUploadResponse
}

export const deleteResourceApi = async (
	materialId: string,
	resourceId: string
) => {
	const { data } = await $authHost.delete(
		`/learning/${materialId}/resources/${resourceId}`
	)
	return data
}

export const deleteFileApi = async (fileUrl: string) => {
	const { data } = await $authHost.delete('/learning/file', {
		params: { fileUrl }
	})
	return data
}
